import React, { useState, useEffect } from "react";
import { useUserAuth } from "../../../context/userAuthContext";
import {
  Users,
  Search,
  Shield,
  UserCheck,
  UserX,
  UserCog,
  Mail,
  Calendar,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  History,
} from "lucide-react";

const API_BASE = "https://us-central1-xfinance-12e10.cloudfunctions.net";

const UserManagementPanel = () => {
  const { user } = useUserAuth();

  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("all");
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleHistory, setShowRoleHistory] = useState(false);
  const [roleHistory, setRoleHistory] = useState([]);

  useEffect(() => {
    if (user) {
      loadUsers();
      loadStatistics();
    }
  }, [user, filterRole]);

  const loadUsers = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        userId: user.uid,
        ...(filterRole !== "all" && { type: filterRole }),
      });

      const response = await fetch(`${API_BASE}/userManagementAPI?${params}`);
      const data = await response.json();

      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Error loading users:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    if (!user) return;

    try {
      const response = await fetch(
        `${API_BASE}/userManagementAPI?userId=${user.uid}&action=statistics`
      );
      const data = await response.json();

      if (data.success) {
        setStatistics(data.statistics);
      }
    } catch (error) {
      console.error("Error loading statistics:", error);
    }
  };

  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      loadUsers();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE}/userManagementAPI?userId=${user.uid}&action=search&search=${encodeURIComponent(searchTerm)}`
      );
      const data = await response.json();

      if (data.success) {
        setUsers(data.users || []);
      }
    } catch (error) {
      console.error("Error searching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateUserRole = async (targetUserId, newRole, reason) => {
    if (!reason || reason.trim() === "") {
      alert("Por favor proporciona una razón para el cambio de rol");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/userManagementAPI?userId=${user.uid}&targetUserId=${targetUserId}&action=role`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newRole, reason }),
        }
      );

      const data = await response.json();

      if (data.success) {
        loadUsers();
        loadStatistics();
        alert("Rol actualizado exitosamente");
        setSelectedUser(null);
      } else {
        alert(data.error || "Error al actualizar rol");
      }
    } catch (error) {
      console.error("Error updating role:", error);
      alert("Error al actualizar rol");
    }
  };

  const loadRoleHistory = async (userId) => {
    try {
      const response = await fetch(
        `${API_BASE}/userManagementAPI?userId=${user.uid}&targetUserId=${userId}&action=roleHistory`
      );
      const data = await response.json();

      if (data.success) {
        setRoleHistory(data.roleHistory || []);
        setShowRoleHistory(true);
      }
    } catch (error) {
      console.error("Error loading role history:", error);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      admin: {
        icon: Shield,
        color: "bg-purple-100 text-purple-800 border-purple-200",
        label: "Administrador",
      },
      asesor: {
        icon: UserCheck,
        color: "bg-blue-100 text-blue-800 border-blue-200",
        label: "Asesor",
      },
      usuario: {
        icon: UserCog,
        color: "bg-slate-100 text-slate-800 border-slate-200",
        label: "Usuario",
      },
    };

    const config = roleConfig[role] || roleConfig.usuario;
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${config.color}`}
      >
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  const filteredUsers = users.filter((u) => {
    const matchesSearch =
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.uid?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-50 via-white to-slate-50">
        <div className="text-center bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-slate-200">
          <AlertCircle className="w-16 h-16 text-[#096365] mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Acceso no autorizado</h2>
          <p className="text-[#096365]">Solo administradores pueden acceder</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 pb-20 sm:pb-8">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-lg border-b border-slate-200/50 sticky top-16 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 sm:py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-lg shadow-md">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-[#218e01] to-[#5fc500] bg-clip-text text-transparent">
                Gestión de Usuarios y Asesores
              </h1>
              <p className="text-xs text-slate-600">Administra roles y permisos de usuarios</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">Total Usuarios</span>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-900">{statistics.users?.total || 0}</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Usuarios</span>
                <UserCog className="w-4 h-4 text-slate-600" />
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {statistics.users?.usuarios || 0}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 border border-blue-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-700">Asesores</span>
                <UserCheck className="w-4 h-4 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-blue-800">
                {statistics.users?.asesores || 0}
              </p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 border border-purple-200 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-purple-700">Administradores</span>
                <Shield className="w-4 h-4 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-purple-800">
                {statistics.users?.admins || 0}
              </p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-md border border-slate-200/50 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && searchUsers()}
                placeholder="Buscar por email o nombre..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
              />
            </div>
            <button
              onClick={searchUsers}
              className="px-4 py-2 bg-gradient-to-r from-[#218e01] to-[#5fc500] text-white rounded-lg font-semibold hover:shadow-lg transition-all"
            >
              Buscar
            </button>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] focus:border-[#5fc500] transition-all bg-white"
            >
              <option value="all">Todos los roles</option>
              <option value="usuario">Usuario</option>
              <option value="asesor">Asesor</option>
              <option value="admin">Administrador</option>
            </select>
          </div>
        </div>

        {/* Users List */}
        {loading ? (
          <div className="text-center py-8">
            <div className="w-8 h-8 border-2 border-[#5fc500] border-t-transparent rounded-full animate-spin mx-auto"></div>
          </div>
        ) : filteredUsers.length > 0 ? (
          <div className="space-y-3">
            {filteredUsers.map((userItem) => (
              <div
                key={userItem.uid}
                className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-r from-[#218e01] to-[#5fc500] rounded-full flex items-center justify-center text-white font-bold">
                      {userItem.displayName?.charAt(0)?.toUpperCase() || "U"}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-bold text-slate-900">
                          {userItem.displayName || "Sin nombre"}
                        </h3>
                        {getRoleBadge(userItem.type || "usuario")}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <span>{userItem.email || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>ID: {userItem.uid.substring(0, 8)}...</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => loadRoleHistory(userItem.uid)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Ver historial de roles"
                    >
                      <History className="w-5 h-5" />
                    </button>
                    <select
                      onChange={(e) => {
                        const newRole = e.target.value;
                        if (newRole !== (userItem.type || "usuario")) {
                          const reason = prompt(
                            `Razón para cambiar el rol a "${newRole}":`
                          );
                          if (reason) {
                            updateUserRole(userItem.uid, newRole, reason);
                          }
                          e.target.value = userItem.type || "usuario"; // Reset select
                        }
                      }}
                      className="px-3 py-1 text-xs border border-slate-300 rounded-lg focus:ring-1 focus:ring-[#5fc500] bg-white"
                      defaultValue={userItem.type || "usuario"}
                    >
                      <option value="usuario">Usuario</option>
                      <option value="asesor">Asesor</option>
                      <option value="admin">Administrador</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Users className="w-12 h-12 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No se encontraron usuarios</p>
          </div>
        )}
      </div>

      {/* Role History Modal */}
      {showRoleHistory && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">Historial de Roles</h2>
              <button
                onClick={() => {
                  setShowRoleHistory(false);
                  setRoleHistory([]);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <XCircle className="w-5 h-5 text-slate-600" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {roleHistory.length > 0 ? (
                roleHistory.map((entry, index) => {
                  const date = entry.timestamp?.toDate
                    ? entry.timestamp.toDate()
                    : entry.timestamp
                      ? new Date(entry.timestamp.seconds * 1000)
                      : new Date();

                  return (
                    <div key={index} className="border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-900">
                          {entry.changedByEmail}
                        </span>
                        <span className="text-xs text-slate-500">
                          {date.toLocaleDateString("es-MX", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <div className="mb-2">
                        <span className="text-xs font-medium text-slate-600">Rol: </span>
                        {getRoleBadge(entry.oldRole)}
                        <span className="mx-2 text-slate-400">→</span>
                        {getRoleBadge(entry.newRole)}
                      </div>
                      {entry.reason && (
                        <div className="mt-2 text-xs text-slate-600">
                          <span className="font-medium">Razón: </span>
                          <span>{entry.reason}</span>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <p className="text-center text-slate-500 py-8">No hay historial disponible</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;

